import { useCallback } from 'react';
import { addRecentWorkspaceItem } from '../../utils/recentWorkspaceItems';

/**
 * Explicit new-tab actions (middle-click / context menu / "+" button).
 */
export function useTabOpenActions({
  openTab,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  clearActiveView,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  setIsLoadingItem,
  preferences,
  VIEWS,
}) {
  const handleOpenInNewTab = useCallback(async (item, type = 'campaign', options = {}) => {
    const label = item.title || item.name;
    openTab({
      type,
      targetId: item.id,
      label,
      icon: type === 'doc' ? null : item.icon,
      viewMode: type === 'doc' ? null : (options.view || preferences.defaultView || 'flow'),
      allowDuplicate: true, // Always allow duplicates when explicitly opening in new tab
      ...(type === 'doc' ? { docStack: [item.id], docScrollById: {} } : {}),
    });

    addRecentWorkspaceItem({
      type,
      id: item.id,
      label,
      icon: item.icon,
    });

    if (type === 'campaign') {
      const selectOptions = {};
      if (options.missionId) selectOptions.missionId = options.missionId;
      if (options.view) selectOptions.view = options.view;
      await handleSelectSprint(item, selectOptions);
    } else if (type === 'template') {
      await handleSelectTemplate(item);
    } else if (type === 'view') {
      await handleSelectView(item);
    } else if (type === 'doc') {
      setCurrentView?.(VIEWS.DOCS);
      requestAnimationFrame(() => {
        window.dispatchEvent(
          new CustomEvent('fokus:open-doc', { detail: { docId: item.id } })
        );
      });
    }
  }, [openTab, preferences.defaultView, handleSelectSprint, handleSelectTemplate, handleSelectView, setCurrentView, VIEWS]);

  // Handle "+" button — open a blank New Tab page (browser-style)
  const handleNewTab = useCallback(() => {
    // Clear workspace selection immediately so the new tab never flashes prior content
    clearActiveView?.();
    setSelectedSprintId(null);
    setSelectedTemplateId(null);
    setCurrentView(VIEWS.EMPTY);
    setIsLoadingItem?.(false);

    openTab({
      type: 'blank',
      targetId: null,
      label: 'New Tab',
      icon: null,
    });
  }, [
    openTab,
    clearActiveView,
    setSelectedSprintId,
    setSelectedTemplateId,
    setCurrentView,
    setIsLoadingItem,
    VIEWS,
  ]);

  return { handleOpenInNewTab, handleNewTab };
}
